import { api } from './api';
import type { PaginatedRagResponse, Rag, RagQueryResult, RagRetrievalConfig, RagVersion } from '../types';

interface ApiResponse<T> { success: boolean; code: number; message: string; data: T }

export interface RagListParams {
  page?: number; page_size?: number; search?: string; status?: string; include_deleted?: boolean;
  sort_by?: string; sort_order?: 'asc' | 'desc'; filters?: string; app_id?: string;
  include_global?: boolean; // also include core/global rows (Show core toggle)
}
export interface RagUpsertRequest {
  appId?: string; ragCode?: string; name?: string; description?: string | null;
  defaultEvalSuiteRef?: string | null; activationPolicy?: { requireEvalPass: boolean }; status?: string;
}
export interface RagVersionCreateRequest {
  pipelineArtifactRef: string;
  retrievalConfig?: Record<string, unknown>;
  citationConfig?: Record<string, unknown>;
  generationConfig?: { enabled: boolean; promptRef?: { promptId?: string | null; version?: number | null }; modelRef?: string | null; maxTokens?: number | null };
  notes?: string | null;
}
export interface RagQueryRequest { query: string; version?: number | null; filters?: Record<string, unknown> | null }

/** One thing that references a RAG — enough to name it and link to it. */
export interface RagDependentItem { id: string | null; name: string; appId: string | null }
/** A kind of dependent (agents, conversations, eval suites) with a sample. */
export interface RagDependentGroup {
  type: 'agent' | 'conversation' | 'eval_suite';
  label: string; count: number; items: RagDependentItem[];
}
/** The downstream dependency graph of a RAG — what embeds or targets it. */
export interface RagDependents { ragId: string; ragCode: string | null; total: number; groups: RagDependentGroup[] }

/** The active version's knowledge side — KB → pipeline → artifact → chunking. */
export interface RagOverviewKnowledge {
  artifactRef?: string | null; artifactCode?: string | null; artifactVersion?: number | null;
  pipelineRef?: string | null; pipelineCode?: string | null; pipelineName?: string | null;
  kbRef?: string | null; kbName?: string | null;
  embeddingModel?: string | null; embeddingDimensions?: number | null;
  chunkCount?: number | null; sourceCount?: number | null;
  provider?: string | null; indexName?: string | null;
  chunking?: { strategy?: string; chunkSize?: number; chunkOverlap?: number; scope?: string } | null;
  parser?: Record<string, unknown> | null;
}
export interface RagOverviewGeneration {
  enabled?: boolean | null;
  promptRef?: string | null; promptName?: string | null; promptVersion?: number | null;
  modelRef?: string | null; modelName?: string | null; maxTokens?: number | null;
}
export interface RagOverviewEvaluation {
  suiteRef?: string | null; suiteName?: string | null; lastEvalScore?: number | null; requireEvalPass?: boolean;
}
/** The name-resolved picture of what a RAG answers with (active version). */
export interface RagOverview {
  ragId: string; ragCode: string | null; name: string | null; activeVersion?: number | null;
  evaluation: RagOverviewEvaluation;
  knowledge: RagOverviewKnowledge | null;
  retrieval: RagRetrievalConfig | null;
  generation: RagOverviewGeneration | null;
}

/** One production query this RAG answered (from rag_query). */
export interface RagQueryHistoryItem {
  id: string;
  query: string | null;
  version: number | null;
  versionStatus: string | null;
  generationEnabled: boolean | null;
  model: string | null;
  sourceCount: number | null;
  chunkCount: number | null;
  cost: number | null;
  currency: string | null;
  totalMs: number | null;
  traceId: string | null;
  actorUserId: string | null;
  createdAt: string | null;
}

export const ragsService = {
  list: async (params?: RagListParams): Promise<PaginatedRagResponse> =>
    (await api.get<PaginatedRagResponse>('/api/v1/rags/list', { params })).data,
  getById: async (id: string): Promise<Rag> => (await api.get<ApiResponse<Rag>>(`/api/v1/rags/get/${id}`)).data.data,
  create: async (data: RagUpsertRequest): Promise<Rag> => (await api.post<ApiResponse<Rag>>('/api/v1/rags/create', data)).data.data,
  update: async (id: string, data: RagUpsertRequest): Promise<Rag> => (await api.put<ApiResponse<Rag>>(`/api/v1/rags/update/${id}`, data)).data.data,
  softDelete: async (id: string) => (await api.delete<ApiResponse<{ _id: string }>>(`/api/v1/rags/delete/${id}`)).data.data,
  restore: async (id: string): Promise<Rag> => (await api.post<ApiResponse<Rag>>(`/api/v1/rags/restore/${id}`)).data.data,

  listVersions: async (ragId: string, status?: string): Promise<RagVersion[]> =>
    (await api.get<ApiResponse<RagVersion[]>>(`/api/v1/rags/${ragId}/versions`, { params: status ? { status } : undefined })).data.data,
  getVersion: async (ragId: string, version: number): Promise<RagVersion> =>
    (await api.get<ApiResponse<RagVersion>>(`/api/v1/rags/${ragId}/versions/${version}`)).data.data,
  createVersion: async (ragId: string, data: RagVersionCreateRequest): Promise<RagVersion> =>
    (await api.post<ApiResponse<RagVersion>>(`/api/v1/rags/${ragId}/versions`, data)).data.data,
  validateVersion: async (ragId: string, version: number): Promise<{ ok: boolean; problems: string[]; version: RagVersion }> =>
    (await api.post<ApiResponse<{ ok: boolean; problems: string[]; version: RagVersion }>>(`/api/v1/rags/${ragId}/versions/${version}/validate`)).data.data,
  activateVersion: async (ragId: string, version: number): Promise<RagVersion> =>
    (await api.post<ApiResponse<RagVersion>>(`/api/v1/rags/${ragId}/versions/${version}/activate`)).data.data,
  rollback: async (ragId: string): Promise<RagVersion> =>
    (await api.post<ApiResponse<RagVersion>>(`/api/v1/rags/${ragId}/rollback`)).data.data,
  /** Retire the active version so nothing serves queries (the RAG can then be deleted). */
  deactivate: async (ragId: string): Promise<{ ragRef: string; retiredVersion: number | null }> =>
    (await api.post<ApiResponse<{ ragRef: string; retiredVersion: number | null }>>(`/api/v1/rags/${ragId}/deactivate`)).data.data,

  /** Downstream dependents: agents, conversations and eval suites that use this RAG. */
  getDependents: async (ragId: string): Promise<RagDependents> =>
    (await api.get<ApiResponse<RagDependents>>(`/api/v1/rags/${ragId}/dependents`)).data.data,

  /** The active version's name-resolved config (KB, pipeline, chunking, retrieval, generation). */
  getOverview: async (ragId: string): Promise<RagOverview> =>
    (await api.get<ApiResponse<RagOverview>>(`/api/v1/rags/${ragId}/overview`)).data.data,

  /** This RAG's production query ledger (newest first), unwrapped to rows + total. */
  listQueries: async (
    ragId: string, params?: { page?: number; page_size?: number },
  ): Promise<{ items: RagQueryHistoryItem[]; total: number }> => {
    const r = await api.get<{ data: RagQueryHistoryItem[]; pagination?: { total?: number } }>(
      `/api/v1/rags/${ragId}/queries`, { params },
    );
    const items = Array.isArray(r.data.data) ? r.data.data : [];
    return { items, total: r.data.pagination?.total ?? items.length };
  },

  /* `traceId` is optional and is the ONLY handle a RAG answer has: the query is
     not persisted, so feedback on it anchors to the request that produced it.
     Passing it in means the screen knows the id before the answer arrives. */
  retrieve: async (ragRef: string, body: RagQueryRequest, traceId?: string): Promise<RagQueryResult> =>
    (await api.post<ApiResponse<RagQueryResult>>(`/api/v1/rags/${ragRef}/retrieve`, body,
      traceId ? { headers: { 'x-trace-id': traceId } } : undefined)).data.data,
  query: async (ragRef: string, body: RagQueryRequest, traceId?: string): Promise<RagQueryResult> =>
    (await api.post<ApiResponse<RagQueryResult>>(`/api/v1/rags/${ragRef}/query`, body,
      traceId ? { headers: { 'x-trace-id': traceId } } : undefined)).data.data,
};
